import React, { createContext, useContext, useState } from "react"
import { postWithDependencyInjection } from "./EndpointProvider"
import { useAuthProvider } from "./AuthProvider"
import { useOnMount } from "../Hooks/useOnMount"

export type EnvironmentVariables = {
	CNS: boolean // whether upload workflow supports CNS directories
	DISABLE_CHECK_INACTIVITY: boolean // prevents automatic logging out after inactivity period
	EDF: boolean // whether upload workflow supports EDF files
	ENCRYPT: boolean // whether upload workflow is encrypted
	FORM: boolean // whether upload workflow supports forms
	MEDICATIONS: boolean // whether upload workflow supports medication files
	NATUS: boolean // whether upload workflow supports natus directories
	PHI_WARNING: boolean // whether the upload workflow shows terms & conditions and phi warning
	DASHBOARD_ORIGIN_URL: string // workspace url for dashboard repository
	QUERY_ORIGIN_URL: string // workspace url for moberg-data-query-api repository
	IAM_ORIGIN_URL: string // workspace url for iam repository
	PATIENT_ID_LENGTH: number // maximum length for patient ID in upload workflow
	PATIENT_ID_MIN_LENGTH: number // minimum length for patient ID; defaults to PATIENT_ID_LENGTH (exact length) when unset
	PATIENT_ID_TYPE: string // alphanumeric, numeric, etc.
	LIVE_REVIEW_REAL_TIME_DELAY_MS: number // How far live review mode is behind real-time (in milliseconds)
	LIVE_REVIEW_READ_AHEAD_BUFFER_MS: number // How far live review mode reads ahead of real-time (in milliseconds)
	FEATURE_FLAG_EEG_MONTAGE_ECG_CHANNEL: boolean
	FEATURE_FLAG_EEG_MONTAGE_EMPTY_CHANNEL: boolean
	FEATURE_FLAG_PERSYST: boolean // Whether or not Persyst features are enabled
	FEATURE_FLAG_SCREENSHOTS: boolean // Whether or not screenshot export is enabled
	FEATURE_FLAG_ADT_INTEGRATION: boolean // Whether of not the system is configured to use an ADT database to track patients
	FEATURE_FLAG_TIME_SELECTOR: boolean // Whether or not the manual time range selector is enabled
	FEATURE_FLAG_DARK_MODE: boolean // Whether or not the dark mode toggle is enabled
	FEATURE_FLAG_EEG_MONTAGE_AVG_REF: boolean // Whether or not EEG average reference montage is enabled
	FEATURE_FLAG_CPPOPT: boolean // Whether or not CPPOpt plot and analytics are enabled
	FEATURE_FLAG_MAPOPT: boolean // Whether or not MAPOpt plot and analytics are enabled
	FEATURE_FLAG_HISTOGRAM: boolean // Whether or not Histogram visualization is enabled
	FEATURE_FLAG_SCATTER_PLOT: boolean // Whether or not Scatter Plot visualization is enabled
	FEATURE_FLAG_SD_DETECTION: boolean // Whether or not SD Detection visualization is enabled
	FEATURE_FLAG_EVENT_REVIEW: boolean // Whether or not Event Review visualization is enabled
	FEATURE_FLAG_CNS_ENVISION_EVENTS: boolean // Whether or not CNS Envision Events are exposed as read-only annotation sessions
	FEATURE_FLAG_PRX: boolean // Whether or not PRx analytics are enabled
	FEATURE_FLAG_COX: boolean // Whether or not COx analytics are enabled
	FEATURE_FLAG_QEEG_DSA_ANALYTIC: boolean // Whether or not the qEEG DSA analytic is enabled
	FEATURE_FLAG_QEEG_DSA_SOURCE: boolean // Whether or not pre-computed (offline) qEEG DSA source modalities can be rendered
	FEATURE_FLAG_ROLLING_AVERAGE: boolean // Whether or not Rolling Average post-processing is enabled
	FEATURE_FLAG_REMOTE_MONITORING: boolean // Whether or not Remote Monitoring mobile interface is enabled
	FEATURE_FLAG_EXPORT: boolean // Whether or not the patient data export feature is enabled
	SITE_ID: number
	PROJECT_ID: number
	SANDBOX_URL: string // workspace URL for the Analytics Studio (JupyterHub sandbox)
	FEATURE_FLAG_SANDBOX: boolean // Whether or not the Analytics Studio quick-access button is shown
	FEATURE_FLAG_CLARA: boolean // Whether or not the Clara assistant is enabled
}

type EnvironmentVariablesContextValue = {
	environmentVariables: EnvironmentVariables
	deployment: string
	asDevelopmentUrl: (url: string, port?: string) => string
}

export enum Deployment {
	DEVELOPMENT = "DEVELOPMENT",
	CLOUD = "CLOUD",
	CLINICAL = "CLINICAL"
}

const EnvironmentVariablesContext = createContext<EnvironmentVariablesContextValue>({} as any)

export const EnvironmentVariablesProvider: React.FC = ({ children }) => {
	const authProvider = useAuthProvider()
	const [environmentVariables, setEnvironmentVariables] = useState<EnvironmentVariables>()
	const configuredDeployment = window.env.REACT_APP_DEPLOYMENT
	const isDev = !configuredDeployment || configuredDeployment === Deployment.DEVELOPMENT
	const deploymentOverride = isDev ? localStorage.getItem("DEPLOYMENT_MODE_OVERRIDE") as Deployment | null : null
	const deployment = deploymentOverride ?? configuredDeployment ?? Deployment.CLOUD
	
	let IAM_URL = window.env.REACT_APP_IAM_ORIGIN_URL

	if (IAM_URL && deployment === Deployment.DEVELOPMENT) {
		IAM_URL = asDevelopmentUrl(IAM_URL, import.meta.env.REACT_APP_IAM_PORT)
	}

	useOnMount(() => {
		postWithDependencyInjection(authProvider?.token, window.env.REACT_APP_WORKSPACE_NAME, { LINK: `${IAM_URL}/v1/workspace/get_workspace_env`, OUTPUT_TYPE: "json" })
			.then((environment: EnvironmentVariables) => setEnvironmentVariables(environment))
			.catch(error => alert(`There was an unrecoverable error in the application. ${error}`))
	})

	function asDevelopmentUrl(url: string, port?: string) {
		const urlObj = new URL(url)
		urlObj.hostname = import.meta.env.REACT_APP_DEV_HOST ?? "localhost"
		if (port) urlObj.port = port
		const urlString = urlObj.toString()
		return urlString.slice(0, urlString.length - 1) // There is an extra slash at the end.
	}

	if (!environmentVariables) {
		return null
	}

	return (
		<EnvironmentVariablesContext.Provider value={{ environmentVariables, deployment, asDevelopmentUrl }}>
			{children}
		</EnvironmentVariablesContext.Provider>
	)
}

export const useEnvironmentVariablesProvider = () => useContext(EnvironmentVariablesContext)
